import { v4 as uuid } from 'uuid';
import { objectFlip } from '../utils/functions';
import DialogConditionDTO, { MAP_CONDITION_FROM_WATSON } from './DialogConditionDTO';
import DialogNextStepDTO, { WATSON_DIALOG_NEXT_STEP_BEHAVIOR } from './DialogNextStepDTO';
import DialogOutputDTO from './DialogOutputDTO';
import DialogSlotsDTO from './DialogSlotsDTO';

export const CHANNEL_FROM_WATSON = {
  web: 'WebChat',
  whatsapp: 'WhatsApp',
};

export const CHANNEL_TO_WATSON = objectFlip(CHANNEL_FROM_WATSON);

const getChannelsEnabled = (conditions) => {
  let channels = [];
  channels = conditions
    .filter(
      (condition) => condition.type === MAP_CONDITION_FROM_WATSON.contextVariable && condition.object === 'sb_channel',
    ).map(
      (c) => {
        let value = c.comparedValue;
        Object.entries(CHANNEL_FROM_WATSON).forEach(
          ([channel, sinonym]) => { value = value.replace(channel, sinonym); },
        );
        return value;
      },
    );

  if (channels.length === 0) return Object.values(CHANNEL_FROM_WATSON);
  return channels;
};

export default class DialogDTO {
  constructor(dialog = null) {
    if (!dialog) {
      this.id = `node_${uuid()}`;
      this.title = '';
      this.type = 'standard';
      this.description = '';
      this.conditions = '';
      this.parent = null;
      this.previousSibling = null;
      this.workspaceId = null;
      return this;
    }

    const dialogType = dialog.type ? dialog.type.toLowerCase() : null;
    if (dialogType !== 'standard' && dialogType !== 'folder' && dialogType !== 'frame') return null;
    this.id = dialog.id;
    this.title = dialog.title ? dialog.title.trim() : '';
    this.previousSibling = dialog.previousSibling;
    this.parent = dialog.parent;
    this.type = dialogType;
    this.outputs = dialog.outputs;
    this.conditions = dialog.conditions;

    if (dialog.nextStep && dialog.nextStep.behavior === WATSON_DIALOG_NEXT_STEP_BEHAVIOR.JUMP_TO) {
      this.nextStep = new DialogNextStepDTO(dialog.nextStep).asInput();
    }

    if (dialog.outputs) {
      this.outputs = DialogOutputDTO.buildList(dialog.outputs);
    } else {
      this.outputs = [];
    }

    if (dialog.conditions) {
      const allConditions = DialogConditionDTO.buildList(dialog.conditions);
      this.enabledChannels = getChannelsEnabled(allConditions);
      this.dialogConditions = allConditions
        .filter((condition) => condition.type !== MAP_CONDITION_FROM_WATSON.contextVariable || condition.object !== 'sb_channel');
    } else {
      this.dialogConditions = [];
      this.enabledChannels = Object.values(CHANNEL_FROM_WATSON);
    }

    if (dialog.slots) {
      this.slots = DialogSlotsDTO.buildList(dialog.slots);
    }

    if (dialogType === 'folder') {
      if (this.outputs) delete this.outputs;
    }

    if (this.previousSibling === -1) this.previousSibling = null;
    if (this.parent === 'root') this.parent = null;
  }

  static buildDialogFlow = (fetchedDialogs) => {
    const dialogs = fetchedDialogs.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});

    const dialogsGraph = {};
    let rootId = null;
    let untitleCount = 1;
    Object.keys(dialogs).forEach((key) => {
      const dialog = new DialogDTO(dialogs[key]);
      if (dialog.id) {
        if (dialog.title.trim() === '') {
          dialog.untitlePlaceholder = `Sem título (${untitleCount})`;
          untitleCount += 1;
        }

        dialogsGraph[key] = !dialogsGraph[key]
          ? dialog
          : {
            ...dialogsGraph[key],
            ...dialog,
          };

        if (dialog.previousSibling) {
          dialogsGraph[dialog.previousSibling] = {
            ...dialogsGraph[dialog.previousSibling],
            nextSibling: dialog.id,
          };
        } else if (dialog.parent) {
          dialogsGraph[dialog.parent] = {
            ...dialogsGraph[dialog.parent],
            firstChild: dialog.id,
          };
        } else {
          rootId = dialog.id;
        }
      }
    });

    const dialogsSequential = {};

    let order = 0;
    const graphToSequential = (dialogId) => {
      const dialog = dialogsGraph[dialogId];
      dialog.order = order;
      dialogsSequential[dialogId] = dialog;
      order += 1;
      if (dialog.firstChild) {
        graphToSequential(dialog.firstChild);
      }
      if (dialog.nextSibling) {
        graphToSequential(dialog.nextSibling);
      }
    };

    graphToSequential(rootId);

    return dialogsSequential;
  }
}
